/**
 * DashboardPage — role-discriminated dashboard renderer.
 *
 * Reads user.role from useAuth() and data from useDashboard().
 * Renders the appropriate role-specific dashboard component.
 *
 * Loading state: spinner while API call is in-flight.
 * Error state: error message with retry option.
 *
 * Role mapping:
 *   super_admin / sales_admin → AdminDashboard
 *   sales_rep                 → SalesRepDashboard
 *   reseller                  → ResellerDashboard
 *   freelance                 → FreelanceDashboard
 *   editor                    → EditorDashboard (minimal)
 */

import { useAuth } from '@/lib/auth'
import { useDashboard } from '@/hooks/useDashboard'
import { AdminDashboard } from './AdminDashboard'
import { SalesRepDashboard } from './SalesRepDashboard'
import { ResellerDashboard } from './ResellerDashboard'
import { FreelanceDashboard } from './FreelanceDashboard'
import type {
  AdminDashboardMetrics,
  SalesRepDashboardMetrics,
  ResellerDashboardMetrics,
  FreelanceDashboardMetrics,
} from '@/hooks/useDashboard'

// ── Loading spinner ───────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p className="text-sm text-gray-500">Cargando dashboard...</p>
      </div>
    </div>
  )
}

// ── Error state ───────────────────────────────────────────────────────────────

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="text-gray-700">No se pudieron cargar las metricas del dashboard.</p>
        <button
          onClick={onRetry}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Reintentar
        </button>
      </div>
    </div>
  )
}

// ── Editor placeholder ────────────────────────────────────────────────────────

function EditorDashboard() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">1-Klick Sales Panel</h2>
        <p className="mt-2 text-sm text-gray-500">
          Tu rol de editor tiene acceso de solo lectura al sistema.
        </p>
      </div>
    </div>
  )
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth()
  const { data, isLoading, isError, refetch } = useDashboard()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isError || !data) {
    return <ErrorState onRetry={refetch} />
  }

  // Type-narrow on role to route to the correct dashboard component
  switch (data.role) {
    case 'super_admin':
    case 'sales_admin':
      return <AdminDashboard metrics={data.metrics as AdminDashboardMetrics} />

    case 'sales_rep':
      return <SalesRepDashboard metrics={data.metrics as SalesRepDashboardMetrics} />

    case 'reseller':
      return <ResellerDashboard metrics={data.metrics as ResellerDashboardMetrics} />

    case 'freelance':
      return <FreelanceDashboard metrics={data.metrics as FreelanceDashboardMetrics} />

    case 'editor':
      return <EditorDashboard />

    default:
      // Fallback: show user name and role — should not happen in normal operation
      return (
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-sm text-gray-500">
            Bienvenido, {user?.name ?? 'usuario'}. Rol: {user?.role ?? 'desconocido'}.
          </p>
        </div>
      )
  }
}
